/**
 * Converts a flat path string into a nested error object structure compatible with React Hook Form.
 *
 * React Hook Form expects errors to be nested according to the field path. For example,
 * a field path like "events.primary[0].eventType" needs an error structure like:
 * ```javascript
 * {
 *   events: {
 *     primary: [
 *       {
 *         eventType: { message: 'Error message' }
 *       }
 *     ]
 *   }
 * }
 * ```
 *
 * This function mutates the provided `errors` object to add the error at the correct nested path.
 *
 * @param {Object} errors - The errors object to mutate. This object will be modified in place.
 * @param {string} path - The field path in dot notation, optionally with array indices in brackets (e.g., "events.primary[0].eventType").
 * @param {string} message - The error message to set at the specified path.
 *
 * @example
 * // Simple field
 * const errors = {}
 * setNestedError(errors, 'name', 'Name is required')
 * // Result: { name: { message: 'Name is required' } }
 *
 * @example
 * // Nested field
 * const errors = {}
 * setNestedError(errors, 'person.firstName', 'First name is required')
 * // Result: { person: { firstName: { message: 'First name is required' } } }
 *
 * @example
 * // Array index
 * const errors = {}
 * setNestedError(errors, 'events[0].type', 'Type is required')
 * // Result: { events: [{ type: { message: 'Type is required' } }] }
 *
 * @example
 * // Complex nested path with arrays
 * const errors = {}
 * setNestedError(errors, 'events.primary[0].eventType', 'Event type is required')
 * // Result: { events: { primary: [{ eventType: { message: 'Event type is required' } }] } }
 */
export const setNestedError = (errors, path, message) => {
  if (!path) return
  const segments = path
    .replaceAll(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  let current = errors

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1
    const nextIsIndex = Number.isInteger(Number(segments[index + 1]))

    if (isLast) {
      current[segment] = { message }
      return
    }

    if (!current[segment]) {
      current[segment] = nextIsIndex ? [] : {}
    }
    current = current[segment]
  })
}
